/**
 * Server-corrected clock. Attendance timers subtract server timestamps
 * (clock-in times) from "now", so a wrong laptop clock skews them by the same
 * amount. Every API response carries the server's `timestamp`; we derive the
 * laptop→server offset from it (NTP-style midpoint) and expose `serverNow()`.
 *
 * Only the absolute "what time is it" readings should use this. Relative
 * timing (setTimeout delays, react-timer-hook's internal ticking) must stay on
 * the local clock or the offset gets applied twice.
 */

/** Samples slower than this round trip are too imprecise to trust. */
const MAX_RTT_MS = 5_000;
/** A sample this far from the current offset means the laptop clock moved. */
const RESYNC_THRESHOLD_MS = 2_000;
/** Subscribers only care about meaningful shifts (e.g. date rollover). */
const NOTIFY_THRESHOLD_MS = 1_000;

let offsetMs = 0;
let bestRttMs = Infinity;
let notifiedOffsetMs = 0;
const listeners = new Set<() => void>();

export const serverNow = (): number => Date.now() + offsetMs;

export const getServerClockOffset = (): number => offsetMs;

export const subscribeServerClock = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Feed one response's server timestamp, with the local times the request was
 * sent (`t0`) and received (`t1`). Keeps the lowest-latency sample so the
 * offset doesn't jitter by network noise on every request, but re-syncs
 * immediately if the laptop clock was changed mid-session.
 */
export const recordServerTimestamp = (
  timestamp: unknown,
  t0: number,
  t1: number,
): void => {
  if (typeof timestamp !== "string") return;
  const serverMs = Date.parse(timestamp);
  const rtt = t1 - t0;
  if (Number.isNaN(serverMs) || rtt < 0 || rtt > MAX_RTT_MS) return;

  const sample = serverMs - (t0 + t1) / 2;
  const clockMoved = Math.abs(sample - offsetMs) > RESYNC_THRESHOLD_MS;
  if (rtt >= bestRttMs && !clockMoved) return;

  offsetMs = sample;
  bestRttMs = rtt;

  if (Math.abs(offsetMs - notifiedOffsetMs) >= NOTIFY_THRESHOLD_MS) {
    notifiedOffsetMs = offsetMs;
    listeners.forEach((listener) => listener());
  }
};
